import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Building2, FileCog, Megaphone, MessageSquareText, Search, Settings2, ShieldCheck, Store, Users, X } from 'lucide-react';
import { selectUserType, seledctAlowedpathlist } from '../utility/Redux/profile';
import { setSystemAdminModalStatus } from '../utility/Redux/security';
import { hasPageAccess } from '../utility/pageAccess';

const Settings = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [query, setQuery] = useState('');
  const masters = [
    {
      name: 'Default Setting',
      description: 'Configure general application and billing preferences.',
      icon: Settings2,
      path: '/settings',
      userType: 'MANAGER ADMIN',
      onclick: () => {
        dispatch(setSystemAdminModalStatus(true));
      },
    },
    {
      name: 'Company Master',
      description: 'Manage company information, address and business details.',
      icon: Building2,
      path: '/company_master',
      userType: 'ADMIN ',
      onclick: () => {
        navigate('/company_master');
      },
    },
    {
      name: 'User Master',
      description: 'Create users and manage their access and responsibilities.',
      icon: Users,
      path: '/user_master',
      userType: 'MANAGER ADMIN',
      onclick: () => {
        navigate('/user_master');
      },
    },
    {
      name: 'Shopkeeper Master',
      description: 'Maintain shopkeeper profiles and account information.',
      icon: Store,
      path: '/shopkeeper_master',
      userType: 'MANAGER ADMIN',
      onclick: () => {
        navigate('/shopkeeper_master');
      },
    },
    {
      name: 'Template Master',
      description: 'Set up reusable document and message templates.',
      icon: FileCog,
      path: '/Template_master',
      userType: 'ADMIN',
      onclick: () => {
        navigate('/Template_master');
      },
    },
    {
      name: 'User Scope Master',
      description: 'Control page-level permissions for each user.',
      icon: ShieldCheck,
      path: '/User_scope_master',
      userType: 'ADMIN',
      onclick: () => {
        navigate('/User_scope_master');
      },
    },

    {
      name: 'Message Setup Master',
      description: 'Configure automated messages and communication rules.',
      icon: MessageSquareText,
      path: '/Message_Setup_Master',
      userType: 'ADMIN',

      onclick: () => {
        navigate('/Message_Setup_Master');
      },
    },
    {
      name: 'Announcement Setup',
      description: 'Publish product updates with audience and expiry controls.',
      icon: Megaphone,
      path: '/announcement_setup',
      userType: 'ADMIN',
      onclick: () => {
        navigate('/announcement_setup');
      },
    },
    {
      name: 'WhatsApp Sender Master',
      description: 'Configure approved WhatsApp sender information.',
      icon: MessageSquareText,
      path: '/whatsapp_sender_master',
      userType: 'ADMIN',
      onclick: () => {
        navigate('/whatsapp_sender_master');
      },
    },
  ];
  const userType = useSelector(selectUserType);
  const allowedPages = useSelector(seledctAlowedpathlist);
  const availableSettings = masters.filter((setting) =>
    setting.userType.includes(userType) && hasPageAccess(userType, allowedPages, setting.path)
  );
  const searchText = query.trim().toLowerCase();
  const filteredSettings = availableSettings.filter((setting) =>
    !searchText || `${setting.name} ${setting.description}`.toLowerCase().includes(searchText)
  );

  const openSetting = (setting) => {
    if (setting.onclick) setting.onclick();
    else navigate(setting.path);
  };

  return (
    <main className="h-full overflow-auto bg-slate-50 p-4 text-slate-900 md:p-7">
      <div className="mx-auto max-w-[1400px]">
        <section className="relative overflow-hidden rounded-3xl px-6 py-7 text-white shadow-xl md:px-9 md:py-9" style={{ background: 'radial-gradient(circle at 88% 10%, rgba(249,115,22,0.40), transparent 32%), linear-gradient(135deg, #020617 0%, #0f172a 58%, #431407 100%)' }}>
          <div className="relative flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
            <div><span className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-xs font-bold"><Settings2 size={14} /> Control centre</span><h1 className="text-3xl font-bold tracking-tight">Settings</h1><p className="mt-2 max-w-xl text-sm leading-6 text-slate-300">Manage your company, users, templates, communications and product announcements from one place.</p></div>
            <div className="rounded-2xl border border-white/10 bg-white/10 px-5 py-3 backdrop-blur"><p className="text-2xl font-bold">{availableSettings.length}</p><p className="text-xs text-slate-300">Available settings</p></div>
          </div>
        </section>

        <section className="sticky top-0 z-20 mt-5 rounded-2xl border border-slate-200 bg-white/95 p-3 shadow-sm backdrop-blur">
          <div className="relative max-w-lg"><Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} /><input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search settings…" aria-label="Search settings" className="h-11 w-full rounded-xl border border-slate-300 bg-slate-50 pl-11 pr-10 text-sm font-medium outline-none focus:border-orange focus:bg-white" />{query ? <button type="button" onClick={() => setQuery('')} aria-label="Clear search" className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-slate-400 hover:bg-slate-200"><X size={15} /></button> : null}</div>
        </section>

        <div className="mb-3 mt-6 px-1"><h2 className="text-lg font-bold">Configuration</h2><p className="text-xs text-slate-500">{filteredSettings.length} {filteredSettings.length === 1 ? 'option' : 'options'} found</p></div>
        {filteredSettings.length ? <div className="grid grid-cols-1 gap-4 pb-8 sm:grid-cols-2 xl:grid-cols-3">
          {filteredSettings.map((setting) => {
            const Icon = setting.icon || Settings2;
            return <button type="button" key={setting.name} onClick={() => openSetting(setting)} className="group flex min-h-[180px] flex-col rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-1 hover:border-orange-300 hover:shadow-xl hover:shadow-orange-100/60 focus:outline-none focus:ring-4 focus:ring-orange-100"><div className="flex items-start justify-between"><span className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-700 transition group-hover:bg-orange group-hover:text-white"><Icon size={21} /></span>{setting.userType.trim() === 'ADMIN' ? <span className="rounded-full bg-orange-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-orange-700">Admin</span> : null}</div><h3 className="mt-5 text-base font-bold group-hover:text-orange-700">{setting.name}</h3><p className="mt-2 flex-1 text-sm leading-5 text-slate-500">{setting.description}</p><span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-orange-600">Open setting <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" /></span></button>;
          })}
        </div> : <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center"><Search className="mx-auto text-slate-400" /><h3 className="mt-3 font-bold">No settings found</h3><p className="mt-1 text-sm text-slate-500">Try another search term.</p></div>}
      </div>
    </main>
  );
};

export default Settings;
